import axios, { AxiosResponse } from 'axios'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { encryptAES, decryptAES } from '../util/cipher'
import { setProfile, clearProfileData } from '../redux/action/profile'
import useSnackbarStore from '../stores/useSnackbarStore'
import useLoadingStore from '../stores/useLoadingStore'
import ConfigAPI from '../constant/ConfigAPI'
import { LoginResponse } from './verify/useLogin'

export type Response = { result?: AxiosResponse<any>; error?: any }
export type Service2faParams = {
    verifyToken: string
    email: string
    state: string
}

export type Login2FAResponse = {
    success: boolean
    accessToken: string
    refreshToken: string
}

const useRelogin = () => {
    const snackbarStore = useSnackbarStore()
    const loadingStore = useLoadingStore()
    const dispatch = useDispatch()
    const history = useHistory()

    const callServiceLogin = async (username: string, isyl: string, redirect?: string) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/auth/login`
            const reqParams = {
                username: username,
                password: encryptAES(isyl),
            }
            const result = await axios.post(url, reqParams, ConfigAPI)
            if (result.status === 200) {
                globalThis.sessionStorage.setItem(
                    `${process.env.REACT_APP_ACCESS_TOKEN_NBTC}`,
                    result.headers['x-access-token'],
                )
                globalThis.sessionStorage.setItem(`${process.env.REACT_APP_IS_LOGIN}`, 'true')
                dispatch(setProfile(result.data))
                if (redirect) {
                    history.push(redirect)
                }
            }
            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                dispatch(clearProfileData())
                if (redirect) {
                    history.push(redirect)
                }
            }
            return { error }
        } finally {
            snackbarStore.setOpenSnackbar(false)
            loadingStore.setLoading(false)
        }
    }

    const callServiceLogin2FA = async (
        param: Service2faParams,
        isLoading: boolean = true,
        isRememberLogin: boolean = false,
    ) => {
        try {
            loadingStore.setLoading(isLoading)
            const url = `${process.env.REACT_APP_API}/api/auth/login2fa`
            const result: AxiosResponse<Login2FAResponse> = await axios.post(url, param)

            globalThis.sessionStorage.clear()
            globalThis.localStorage.removeItem(`${process.env.REACT_APP_TOKEN_PROFILE}`)

            if (result.status === 200 && result.data.success) {
                globalThis.sessionStorage.setItem(`${process.env.REACT_APP_ACCESS_TOKEN_NBTC}`, result.data.accessToken)

                const hashCookieToken = encryptAES(JSON.stringify({ ...result.data, isRememberLogin }))
                if (isRememberLogin) {
                    globalThis.localStorage.setItem(`${process.env.REACT_APP_TOKEN_PROFILE}`, hashCookieToken)
                }

                globalThis.sessionStorage.setItem(`${process.env.REACT_APP_TOKEN_PROFILE}`, hashCookieToken)

                callServiceGetProfile('/', true)
            } else {
                history.push('/login?result=error')
            }
        } catch (error) {
            console.log('error ::', error)
            globalThis.sessionStorage.clear()
            globalThis.localStorage.removeItem(`${process.env.REACT_APP_TOKEN_PROFILE}`)
            history.push('/login?result=error')
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceGetProfile = async (redirectUrl: string | null = null, isfromLogin: boolean = false) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/auth/fetchprofile`
            const result: AxiosResponse<LoginResponse> = await axios.get(url)

            if (result.status === 200) {
                dispatch(setProfile(result.data))
                if (redirectUrl) {
                    history.push(redirectUrl)
                }
            }
        } catch (error) {
            console.log('error ::', error)
            globalThis.localStorage.removeItem(`${process.env.REACT_APP_TOKEN_PROFILE}`)
            if (isfromLogin) {
                history.push('/login?result=error')
            }

            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    dispatch(clearProfileData())
                    globalThis.sessionStorage.clear()
                    globalThis.localStorage.removeItem(`${process.env.REACT_APP_TOKEN_PROFILE}`)
                    snackbarStore.setOpenSnackbar(false)
                    history.push('/')
                }
            }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const reLoginForTokenExpired = (redirect?: string) => {
        snackbarStore.setOpenSnackbar(true)
        const username = globalThis.localStorage.getItem(`${process.env.REACT_APP_REMEMBER_USER}`)
        if (username) {
            const isyl = globalThis.localStorage.getItem(`${process.env.REACT_APP_REMEMBER_ISYL}`)
            if (isyl) {
                callServiceLogin(decryptAES(username), decryptAES(isyl), redirect)
            }
        } else {
            dispatch(clearProfileData())
            globalThis.sessionStorage.clear()
            snackbarStore.setOpenSnackbar(false)
            history.push('/')
        }
    }

    const callServiceReLogin = async (redirectUrl: string | null = null, isErrorRedirect: boolean = true) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/auth/relogin`
            let localRefId = globalThis.sessionStorage.getItem(`${process.env.REACT_APP_TOKEN_PROFILE}`)

            if (!localRefId) {
                localRefId = globalThis.localStorage.getItem(`${process.env.REACT_APP_TOKEN_PROFILE}`)
            }

            const refId = localRefId ? JSON.parse(decryptAES(localRefId)) : null

            if (refId) {
                const reqParams = {
                    accessToken: refId.accessToken,
                    refreshToken: refId.refreshToken,
                }

                const result = await axios.post(url, reqParams)

                globalThis.sessionStorage.clear()
                globalThis.localStorage.removeItem(`${process.env.REACT_APP_TOKEN_PROFILE}`)
                if (result.status === 200) {
                    globalThis.sessionStorage.setItem(
                        `${process.env.REACT_APP_ACCESS_TOKEN_NBTC}`,
                        result.data.accessToken,
                    )

                    const hashCookieToken = encryptAES(
                        JSON.stringify({ ...result.data, isRememberLogin: refId.isRememberLogin }),
                    )
                    if (refId.isRememberLogin) {
                        globalThis.localStorage.setItem(`${process.env.REACT_APP_TOKEN_PROFILE}`, hashCookieToken)
                    }

                    globalThis.sessionStorage.setItem(`${process.env.REACT_APP_TOKEN_PROFILE}`, hashCookieToken)

                    callServiceGetProfile(redirectUrl)
                }
                return { result }
            } else {
                dispatch(clearProfileData())
                globalThis.sessionStorage.clear()
                globalThis.localStorage.removeItem(`${process.env.REACT_APP_TOKEN_PROFILE}`)
                snackbarStore.setOpenSnackbar(false)
                if (isErrorRedirect) {
                    history.push('/')
                }
            }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                dispatch(clearProfileData())
                globalThis.sessionStorage.clear()
                globalThis.localStorage.removeItem(`${process.env.REACT_APP_TOKEN_PROFILE}`)
                snackbarStore.setOpenSnackbar(false)
                history.push('/')
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    return {
        callServiceLogin,
        reLoginForTokenExpired,
        callServiceLogin2FA,
        callServiceReLogin,
        callServiceGetProfile,
    }
}

export default useRelogin
